import { useEffect, useState } from "react";

import Swal from "sweetalert2";

import {
    addDestinationToTracker,
    createDestinationFacebook,
    getDestinationFacebook,
} from "helpers/destinations/createDestination";
import { getTrackerDetailNew } from "helpers/trackers/getTrackerEvent";

import SwalAlertComp from "components/SwalAlert";

import { FACEBOOK_BUSINESS_TYPES } from "../DestinationCreation.constants";
import {
    buildFacebookBeExtra,
    buildPixelTypeNoticeHtml,
} from "../DestinationCreation.helpers";

/**
 * Meta CAPI (FB SDK login) and the FBE business setup shared with Meta Custom
 * Audience.
 */
const useFacebookDestination = ({
    language,
    destinationCode,
    bussiness,
    createPixelStatus,
    resetForm,
    completeCreation,
    hostActions,
}) => {
    const [externalBusinessId, setExternalBusinessId] = useState();
    const [beExtra, setBeExtra] = useState();
    const [isLoading, setIsLoading] = useState(false);

    const { trackerId, getTracker, dismiss } = hostActions;

    useEffect(() => {
        if (FACEBOOK_BUSINESS_TYPES.includes(destinationCode)) {
            const fetchExternalBusinessId = async () => {
                const response = await getDestinationFacebook();
                setExternalBusinessId(response?.data?.external_business_id);
            };
            fetchExternalBusinessId();
        }
    }, [destinationCode]);

    useEffect(() => {
        if (externalBusinessId && externalBusinessId.length > 0) {
            const selectedBusiness = bussiness.find(
                (element) => element.selected === true,
            );
            setBeExtra(
                buildFacebookBeExtra(externalBusinessId, selectedBusiness),
            );
        }
    }, [bussiness, externalBusinessId]);

    const showPixelTypeNoticeOrRefresh = async () => {
        const res = await getTrackerDetailNew(trackerId);
        if (
            res?.data?.tracker?.is_crm === false &&
            res?.data?.tracker?.is_consolidated_container === false
        ) {
            const metaConversionsApiDestination =
                res?.data?.tracker?.destinations?.find(
                    (destination) =>
                        destination?.destination_type?.name ===
                        "Meta Conversions Api",
                );
            const pixelId = metaConversionsApiDestination
                ? metaConversionsApiDestination.pixel_id
                : "";
            Swal.fire({
                title: "Pixel Type Notice",
                icon: "warning",
                html: buildPixelTypeNoticeHtml(pixelId),
            });
        } else {
            getTracker?.();
        }
    };

    const attachToTracker = async (destinationIds) => {
        const response = await addDestinationToTracker(
            { destinations: destinationIds },
            trackerId,
        );
        if (response.status) {
            resetForm();
            await showPixelTypeNoticeOrRefresh();
            dismiss();
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message && response.message,
                icon: "error",
            });
        }
    };

    /** `FaceBookSdk` login callback; `accessToken` is the FB user token. */
    const handleLogin = async (accessToken) => {
        setIsLoading(true);
        if (!accessToken) return;
        window.FB.api(
            "fbe_business/fbe_installs?fbe_external_business_id=" +
                externalBusinessId,
            async function (response) {
                if (!(response.data && response.data.length > 0)) {
                    SwalAlertComp({
                        title: language?.delete_swal_modal?.failed ?? "Failed",
                        message:
                            language?.delete_swal_modal
                                ?.error_pixel_found_user_authorize ??
                            "Error. No Pixel found! User cancelled login or did not fully authorize.",
                        icon: "error",
                    });
                    setIsLoading(false);
                    return;
                }

                const destinationCreate = await createDestinationFacebook({
                    access_token: accessToken,
                    pixels: response.data.map((item) => item.pixel_id),
                    extra_data: response.data[0] && response.data[0],
                    external_business_id: externalBusinessId,
                    create_pixel: createPixelStatus,
                });
                if (destinationCreate.status) {
                    const destinationIds = (destinationCreate.data ?? []).map(
                        (item) => item.id,
                    );
                    if (trackerId) {
                        if (destinationCreate?.data?.length > 0) {
                            await attachToTracker(destinationIds);
                        }
                    } else {
                        completeCreation(destinationIds);
                    }
                } else {
                    SwalAlertComp({
                        title: language?.delete_swal_modal?.failed ?? "Failed",
                        message: response.message,
                        icon: "error",
                    });
                }
                setIsLoading(false);
            },
        );
    };

    const reset = () => {
        setExternalBusinessId(undefined);
        setBeExtra(undefined);
        setIsLoading(false);
    };

    return {
        externalBusinessId,
        beExtra,
        isLoading,
        handleLogin,
        reset,
    };
};

export default useFacebookDestination;
